import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import ExperienceProject from '../components/ExperienceProject';
import { formatTechnologies } from '../utils/textUtils';
import './About.css'; // Reuse About page styles

const MONTH_MAP = {
  'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 'Apr': 'April',
  'May': 'May', 'Jun': 'June', 'Jul': 'July', 'Aug': 'August',
  'Sep': 'September', 'Oct': 'October', 'Nov': 'November', 'Dec': 'December',
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === 'Present') return 'Present';
  const parts = dateStr.split('-');
  if (parts.length === 2) {
    const month = MONTH_MAP[parts[0]] || parts[0];
    const year = parts[1].length === 2 ? `20${parts[1]}` : parts[1];
    return `${month} ${year}`;
  }
  return dateStr;
};

const Experience = () => {
  const [experience, setExperience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExperience = async () => {
      try {
        const response = await axios.get('/api/experience');
        setExperience(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching experience data:', err);
        setError('Failed to load experience data. Please try again later.');
        setLoading(false);
      }
    };

    fetchExperience();
  }, []);

  if (loading) {
    return (
      <Container className="py-5 mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading experience information...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <div className="mt-5 pt-4">
      <section className="section">
        <Container>
          <h2 className="section-title text-center mb-5">Professional Experience</h2>
          <div className="section-intro text-center mb-5">
            <p className="lead">
              A track record of delivering impactful solutions across various industries
            </p>
          </div>
          <Row>
            <Col lg={10} className="mx-auto">
              <div className="timeline">
                {experience.map((exp, index) => {
                  const technologies = formatTechnologies(exp.technologies);

                  return (
                    <div className="experience-card mb-5" key={index}>
                      <div className="card-body text-left">
                      <div className="experience-header">
                        <h3 className="experience-position">{exp.position}</h3>
                        <div className="experience-duration">
                          {formatDate(exp.start_date)} - {formatDate(exp.end_date) || 'Present'}
                          {exp.location && (
                            <span className="location-text"> • {exp.location}</span>
                          )}
                        </div>
                      </div>
                      <div className="experience-company mb-3">
                        <div className="company-info">
                          {exp.logo && (
                            <img
                              src={exp.logo}
                              alt={`${exp.company} logo`}
                              className="company-logo-square"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          )}
                          <span className="company-logo-fallback-square" style={{display: exp.logo ? 'none' : 'block'}}>
                            {exp.company.charAt(0).toUpperCase()}
                          </span>
                          <div className="company-details">
                            <span className="company-name">{exp.company}</span>
                            {exp.company_url && (
                              <a
                                href={exp.company_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="company-url"
                              >
                                {exp.company_url}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {exp.description && (
                        <p className="experience-description mb-3">{exp.description}</p>
                      )}
                      
                      {exp.achievements && exp.achievements.length > 0 && (
                        <div className="experience-achievements mb-3">
                          <h5 className="achievements-title">Key Achievements:</h5>
                          <ul className="achievements-list">
                            {exp.achievements.map((achievement, i) => (
                              <li key={i}>{achievement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {technologies.length > 0 && (
                        <div className="experience-technologies mb-3">
                          <h5 className="technologies-title">Technologies:</h5>
                          <div className="experience-tech-badges">
                            {technologies.map((tech, i) => (
                              <span key={i} className="tech-badge experience-tech-badge">{tech}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {exp.projects && exp.projects.length > 0 && (
                        <div className="experience-projects mt-4">
                          <h5 className="projects-title">Key Projects:</h5>
                          {exp.projects.map((project, i) => (
                            <ExperienceProject key={i} project={project} />
                          ))}
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Experience;
